import { createContext, useContext } from "react";
import { UserView } from "./AccountMenu";
import * as React from "react";
import axios from "axios";
import { useSupportedFunctionality } from "./SupportedFunctionality";
import { Patron } from "@tlc/ls2pac/dto/Patron";
import { AccountSummary } from "@tlc/ls2pac/dto/AccountSummary";

interface GetAccountSummaryReturn {
  patron: Patron;
  accountSummary: AccountSummary;
}

export interface AccountHolderData {
  isAuthenticated: Readonly<boolean>;
  user: Readonly<UserView>;
  accountSummary: Readonly<AccountSummary>;
  patron: Readonly<Patron>;
  isConfigAdmin: Readonly<boolean>;
  isAdmin: Readonly<boolean>;
}

export interface AccountContextType extends AccountHolderData {
  canViewDependentActivity: boolean; // TODO hasChildrenAndCanViewDependentActivity
  hasProhibitedListFunctionality: boolean;
  isGuardian: boolean;
}

const DEFAULT_CACHE_EXPIRATION_TIME = 5 * 60 * 1000;

export class Cache<T> {
  private _timestamp: number;
  private _updated: number;

  constructor(
    private data: T,
    private expirationTime: number = DEFAULT_CACHE_EXPIRATION_TIME
  ) {}

  get valid() {
    return (
      !!this._timestamp && Date.now() - this._timestamp < this.expirationTime
    );
  }

  invalidate() {
    // forces a re-request and re-notify on next turn
    this._timestamp = null;
    this._updated = null;
  }

  get updated() {
    return this._updated;
  }

  get current(): Readonly<T> {
    if (this.data === null || this.data === undefined) {
      return null;
    }
    if (typeof this.data === "object" && Array.isArray(this.data)) {
      return [...this.data] as unknown as Readonly<T>;
    }
    if (typeof this.data === "object") {
      // This doesn't prevent manipulation in the case of deeply nested objects and arrays
      return Object.freeze({ ...this.data });
    }
    // Primitives with 'get' are immutable
    return this.data;
  }

  // marked protected instead of private so that Cache can be extended for deeper equality checks
  protected equals(data: T) {
    return data === this.data;
  }

  set(data: T) {
    // if the value is different set the updated timestamp which is used for subscriber notification logic
    if (!this._updated || !this.equals(data)) {
      this._updated = Date.now();
    }
    this.data = data;
    this._timestamp = Date.now();
  }
}

/*
  This data is frequently and concurrently requested throughout the app
  Caching and request sharing are important concerns of this holder
  It also needs to be flushed strategically following specific transactions
*/
export const AccountHolder = (() => {
  class AccountHolder {
    private readonly user = new Cache<UserView>(null);
    private readonly patron = new Cache<Patron>(null);
    private readonly accountSummary = new Cache<AccountSummary>(null);
    private readonly isAuthenticated = new Cache(false, 2000); // shorter cache expiration for isAuthenticated
    private readonly subscribers: ((data: AccountHolderData) => void)[] = [];
    private lastNotified: number;

    private notifySubscribers() {
      // has anything changed since we last notified subscribers?
      if (
        !this.lastNotified ||
        this.accountSummary.updated > this.lastNotified ||
        this.patron.updated > this.lastNotified ||
        this.isAuthenticated.updated > this.lastNotified ||
        this.user.updated > this.lastNotified
      ) {
        const data = this.currentData;
        this.subscribers.forEach((sub) => sub(data));
        this.lastNotified = Date.now();
      }
    }

    private pendingGetUser: Promise<UserView> | null = null;
    private async getUser() {
      if (this.pendingGetUser) {
        console.log("User request pending, returning promise");
        return this.pendingGetUser;
      }

      if (!this.user.valid) {
        console.log("User cache invalid, requesting");
        this.pendingGetUser = (async () => {
          const { data } = await axios.get<UserView>(`/currentUser`);
          this.user.set(data);
          this.pendingGetUser = null;
          console.log("Resolving pending User request");
          return this.user.current;
        })();

        return this.pendingGetUser;
      }

      console.log("Returning cached User");
      return this.user.current;
    }

    private pendingGetAccountSummaryReturn: Promise<GetAccountSummaryReturn> | null =
      null;
    private async getAccountSummaryReturn() {
      if (this.pendingGetAccountSummaryReturn) {
        console.log(
          "GetAccountSummaryReturn request pending, returning promise"
        );
        return this.pendingGetAccountSummaryReturn;
      }

      if (!this.patron.valid || !this.accountSummary.valid) {
        console.log("GetAccountSummaryReturn cache invalid, requesting");
        this.pendingGetAccountSummaryReturn = (async () => {
          try {
            const { data } = await axios.get<GetAccountSummaryReturn>(
              `/account/summary`
            );
            this.patron.set(data.patron);
            this.accountSummary.set(data.accountSummary);
          } catch (e: unknown) {
            this.patron.set(null);
            this.accountSummary.set(null);
          } finally {
            this.pendingGetAccountSummaryReturn = null;
            return {
              patron: this.patron.current,
              accountSummary: this.accountSummary.current,
            };
          }
        })();
        return this.pendingGetAccountSummaryReturn;
      }

      console.log("Returning cached GetAccountSummaryReturn");
      return {
        patron: this.patron.current,
        accountSummary: this.accountSummary.current,
      };
    }

    private pendingGetIsAuthenticated: Promise<boolean> | null = null;
    private async getIsAuthenticated() {
      if (this.pendingGetIsAuthenticated) {
        console.log("isAuthenticated request pending, returning promise");
        return this.pendingGetIsAuthenticated;
      }

      if (!this.isAuthenticated.valid) {
        console.log("isAuthenticated cache invalid, requesting");
        this.pendingGetIsAuthenticated = (async () => {
          const { data } = await axios.get<boolean>(`/isAuthenticated`);
          const changed =
            data !== this.isAuthenticated.current ||
            !this.isAuthenticated.updated; // different value or first request
          this.isAuthenticated.set(data);

          // if the isAuthenticated status changes invalidate dependent caches
          if (changed) {
            this.user.invalidate();
            this.accountSummary.invalidate();
            this.patron.invalidate();
          }

          // if the current user is no longer authenticated
          if (data === false) {
            // set caches to null so they are not unnecessarily requested
            this.user.set(null);
            this.accountSummary.set(null);
            this.patron.set(null);
          }

          this.pendingGetIsAuthenticated = null;
          console.log("Resolving pending isAuthenticated request");
          return this.isAuthenticated.current;
        })();

        return this.pendingGetIsAuthenticated;
      }
      console.log("Returning cached isAuthenticated");
      return this.isAuthenticated.current;
    }

    public get currentData() {
      return Object.freeze({
        isAuthenticated: this.isAuthenticated.current,
        user: this.user.current,
        patron: this.patron.current,
        accountSummary: this.accountSummary.current,
        isConfigAdmin: this.isConfigAdmin,
        isAdmin: this.isAdmin,
      });
    }

    public get hasChildren() {
      return this.patron.current?.children?.length > 0;
    }

    public get isConfigAdmin() {
      const currentUser = this.user.current;
      if (!currentUser) {
        return false;
      }
      const permissions = currentUser.topRole.permissions;
      return (
        permissions.allowPublishToBookriver ||
        permissions.allowPublishToDisplayCase ||
        permissions.allowPublishToSmartGraphic
      );
    }

    public get hasValidPatronId() {
      const currentUser = this.user.current;
      if (!currentUser) {
        return false;
      }
      const { patronId } = currentUser;

      return patronId !== undefined && patronId !== null && patronId > "";
    }

    public get isAdmin() {
      if (!this.user.current) {
        return false;
      }

      return !this.hasValidPatronId;
    }

    public async fetchAccountData(reload: boolean = false) {
      if (reload === true) {
        this.isAuthenticated.invalidate();
        this.user.invalidate();
        this.accountSummary.invalidate();
        this.patron.invalidate();
      }
      await this.getIsAuthenticated();
      await this.getUser();
      await this.getAccountSummaryReturn();
      this.notifySubscribers();
      return this.currentData;
    }

    public async fetchUser(reload: boolean = false) {
      if (reload === true) {
        this.user.invalidate();
      }
      await this.getIsAuthenticated();
      await this.getUser();
      this.notifySubscribers();
      return this.user.current;
    }

    public async fetchPatron(reload: boolean = false) {
      if (reload === true) {
        this.patron.invalidate();
      }
      await this.getIsAuthenticated();
      await this.getAccountSummaryReturn();
      this.notifySubscribers();
      return this.patron.current;
    }

    public async fetchAccountSummary(reload: boolean = false) {
      if (reload === true) {
        this.accountSummary.invalidate();
      }
      await this.getIsAuthenticated();
      await this.getAccountSummaryReturn();
      this.notifySubscribers();
      return this.accountSummary.current;
    }

    public async fetchAccountSummaryAndPatron(reload: boolean = false) {
      if (reload === true) {
        this.patron.invalidate();
        this.accountSummary.invalidate();
      }
      await this.getIsAuthenticated();
      await this.getAccountSummaryReturn();
      this.notifySubscribers();
      return Object.freeze({
        patron: this.patron.current,
        accountSummary: this.accountSummary.current,
      });
    }

    public clearAccountData() {
      this.user.set(null);
      this.patron.set(null);
      this.accountSummary.set(null);
      this.isAuthenticated.set(false);
      this.notifySubscribers();
    }

    /**
     *
     * @param subscriber
     * @returns an unsubscribe function
     */
    public subscribe(subscriber: (data: AccountHolderData) => void) {
      if (
        this.isAuthenticated.valid &&
        this.patron.valid &&
        this.accountSummary.valid &&
        this.user.valid
      ) {
        subscriber(this.currentData);
        this.subscribers.push(subscriber);
      } else {
        this.fetchAccountData().then((data) => {
          // we wait to subscribe until after the request resolves
          // in some cases the request would trigger a notification to subscribers
          // in others (like if only isAuthenticated was invalid but the response value remained the same) it would not trigger a notification
          // this guarantees the new subscriber is notified without being double notified, or re-notifying other subscribers with the same data
          subscriber(data);
          this.subscribers.push(subscriber);
        });
      }

      return () => {
        const index = this.subscribers.indexOf(subscriber);
        if (index !== -1) {
          this.subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
        }
      };
    }
  }

  // TODO add login, logout, and admin verification functions

  return new AccountHolder();
})();

export const AccountContext = createContext<AccountContextType>(null);

export const AccountProvider = ({ children }: React.PropsWithChildren) => {
  const initialData = AccountHolder.currentData;
  const { supportedFunctionality } = useSupportedFunctionality();
  const { useGuardianProhibition, allowGuardianToViewDependentActivity } =
    supportedFunctionality || {};

  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(
    initialData.isAuthenticated
  );
  const [isConfigAdmin, setIsConfigAdmin] = React.useState(
    initialData.isConfigAdmin
  );

  const [isAdmin, setIsAdmin] = React.useState(initialData.isAdmin);
  const [user, setUser] = React.useState<UserView>(initialData.user);
  const [accountSummary, setAccountSummary] = React.useState<AccountSummary>(
    initialData.accountSummary
  );
  const [patron, setPatron] = React.useState<Patron>(initialData.patron);

  const guardianPermissions = React.useMemo(() => {
    const canViewDependentActivity =
      allowGuardianToViewDependentActivity && AccountHolder.hasChildren;
    const hasProhibitedListFunctionality =
      useGuardianProhibition && AccountHolder.hasChildren;
    const isGuardian =
      canViewDependentActivity || hasProhibitedListFunctionality;

    return {
      canViewDependentActivity,
      hasProhibitedListFunctionality,
      isGuardian,
    };
  }, [
    useGuardianProhibition,
    allowGuardianToViewDependentActivity,
    AccountHolder.hasChildren,
  ]);

  React.useEffect(() => {
    const unsubscribeFromAccountHolder = AccountHolder.subscribe((data) => {
      setIsAuthenticated(data.isAuthenticated);
      setUser(data.user);
      setPatron(data.patron);
      setAccountSummary(data.accountSummary);
      setIsConfigAdmin(data.isConfigAdmin);
      setIsAdmin(data.isAdmin);
    });

    return () => {
      unsubscribeFromAccountHolder();
    };
  }, []);

  const contextValue: AccountContextType = {
    isAuthenticated,
    user,
    accountSummary,
    patron,
    ...guardianPermissions,
    isConfigAdmin,
    isAdmin,
  };

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  );
};

export function useAccount() {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error("useAccount must be used within an AccountProvider");
  }

  return context;
}

interface MockAccountProviderProps
  extends React.PropsWithChildren,
    Partial<AccountContextType> {}

export const MockAccountProvider = ({
  isAuthenticated,
  user,
  accountSummary,
  patron,
  children,
  isGuardian,
  canViewDependentActivity,
  hasProhibitedListFunctionality,
  isConfigAdmin,
  isAdmin,
}: MockAccountProviderProps) => {
  const defaultValue: AccountContextType = {
    isAuthenticated: isAuthenticated || false,
    user: user || null,
    accountSummary: accountSummary || null,
    patron: patron || null,
    isGuardian: isGuardian || false,
    canViewDependentActivity: canViewDependentActivity || false,
    hasProhibitedListFunctionality: hasProhibitedListFunctionality || false,
    isConfigAdmin: isConfigAdmin || false,
    isAdmin: isAdmin || false,
  };

  return (
    <AccountContext.Provider value={defaultValue}>
      {children}
    </AccountContext.Provider>
  );
};
